import React from 'react';

import { PLUS, REFRESH } from '../icons';
import { canReviewUpdate } from '../../services/packages';
import { packagePath } from '../../utils/packageAddress';

/**
 * The actions of one bundle group's ⋯ in the Card Library (KPR-1130; epic
 * KPE-035). Each action is a small function of the same context and returns
 * an action object for `buildActionMenu` (src/components/common/actionMenu.js),
 * or null when it does not apply. `buildActionMenu` puts them in the
 * platform's taxonomy order (Go to, Record, Share & export, Danger), so an
 * item that adds an action only adds ONE function to BUNDLE_GROUP_ACTIONS,
 * names its taxonomy `group`, and never reorders anything:
 *
 *   KPR-1133  Keep the cards, stop tracking…   group 'record'
 *   KPR-458   Review update                    group 'navigate' (or 'record')
 *   KPR-1132  Remove bundle…                   group 'destructive' (last, red, confirmed)
 *
 * The context: `{ bundle, collectionId, navigate, onAddAddOn, onDetachBundle, onRemoveBundle, onReviewUpdate }`, where
 * `bundle` is the API's row for the package (`getCollectionPackages`). A group
 * the person cannot act on gets no actions, and so no ⋯ (the caller passes
 * `actionable: false`): the API tells a reader nothing a menu would need.
 *
 * Words: a Discover card or bundle package is a "bundle" here; an element set
 * is never called one.
 */

/** The bundle's Discover page, unless Discover no longer lists it (a retired package answers 404 there). */
function viewInDiscover({ bundle, navigate }) {
	return {
		key: 'view-in-discover',
		group: 'navigate',
		glyph: 'open',
		label: 'View in Discover',
		hidden: bundle.retired === true,
		onClick: () => navigate(packagePath(bundle.key)),
	};
}

/** Today's add-on install dialog for this collection, when the bundle has an add-on this collection lacks. */
function addAnAddOn({ bundle, onAddAddOn }) {
	const missing = (bundle.addOns && bundle.addOns.notInstalled) || [];
	return {
		key: 'add-an-add-on',
		group: 'record',
		icon: <PLUS />,
		label: 'Add an add-on',
		hidden: bundle.retired === true || missing.length === 0,
		onClick: () => onAddAddOn(bundle),
	};
}

/**
 * Opens the bundle's update (KPR-458): every change of the published version,
 * one by one, to take or keep. Shown while the collection is behind the
 * published version — even after *Ignore this update* turned the chip off, so
 * the person can change their mind. Record group: it changes the bundle's cards.
 */
function reviewUpdate({ bundle, onReviewUpdate }) {
	return {
		key: 'review-update',
		group: 'record',
		icon: <REFRESH />,
		label: 'Review update',
		hidden: typeof onReviewUpdate !== 'function' || !canReviewUpdate(bundle),
		onClick: () => onReviewUpdate(bundle),
	};
}

/**
 * Keeps the bundle's cards as the collection's own and stops tracking the
 * bundle (KPR-1133), after a confirm. Record group: nothing is deleted, and
 * unlike Remove it is offered for a retired bundle too (there is nothing
 * left to be updated, but the group would otherwise stay for ever).
 */
function keepCardsStopTracking({ bundle, onDetachBundle }) {
	return {
		key: 'keep-cards-stop-tracking',
		group: 'record',
		glyph: 'duplicate',
		label: 'Keep the cards, stop tracking…',
		hidden: typeof onDetachBundle !== 'function',
		onClick: () => onDetachBundle(bundle),
	};
}

/** Takes the bundle out of this collection, after a preview and a typed word (KPR-1132). Danger: last, red. */
function removeBundle({ bundle, onRemoveBundle }) {
	return {
		key: 'remove-bundle',
		group: 'destructive',
		glyph: 'delete',
		label: 'Remove bundle…',
		onClick: () => onRemoveBundle(bundle),
	};
}

/** In menu order within a taxonomy group. Append here to add an entry. */
export const BUNDLE_GROUP_ACTIONS = Object.freeze([
	viewInDiscover,
	reviewUpdate,
	addAnAddOn,
	keepCardsStopTracking,
	removeBundle,
]);

/** The actions for `buildActionMenu`; none when the person cannot act on the group. */
export function bundleGroupActions({ actionable = false, ...context }) {
	if (!actionable || !context.bundle) return [];
	return BUNDLE_GROUP_ACTIONS.map(make => make(context)).filter(Boolean);
}
